import { useState, type FormEvent } from 'react';
import { useTasks, type TaskFilter } from '@/hooks/use-tasks';
import type { Priority, TaskWithAuthor } from '@/types';
import { Alert, Avatar, Button, cx, Icon, Input, Select } from './ui';

const FILTERS: { value: TaskFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'mine', label: 'Mine' },
  { value: 'open', label: 'Open' },
  { value: 'done', label: 'Done' },
];

const PRIORITY_STYLE: Record<Priority, string> = {
  high: 'bg-danger-soft text-danger',
  normal: 'bg-sunken text-soft',
  low: 'bg-brand-soft text-brand-strong',
};

function timeAgo(iso: string) {
  const s = Math.max(1, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.round(s / 60)}m ago`;
  if (s < 86400) return `${Math.round(s / 3600)}h ago`;
  return new Date(iso).toLocaleDateString();
}

export function TaskBoard({ userId, onTyping, typing }: { userId: string; onTyping: () => void; typing: string[] }) {
  const [filter, setFilter] = useState<TaskFilter>('all');
  const [search, setSearch] = useState('');
  const board = useTasks(userId, filter, search);
  const [title, setTitle] = useState('');
  const [priority, setPriority] = useState<Priority>('normal');
  const [adding, setAdding] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const act = async (fn: () => Promise<void>) => {
    setActionError(null);
    try {
      await fn();
    } catch (e) {
      setActionError((e as Error).message);
    }
  };

  const add = async (e: FormEvent) => {
    e.preventDefault();
    if (!title.trim()) return;
    setAdding(true);
    await act(async () => {
      await board.addTask(title.trim(), priority);
      setTitle('');
      setPriority('normal');
    });
    setAdding(false);
  };

  const s = board.stats;
  const myDone = board.tasks.some((t) => t.user_id === userId && t.done);

  return (
    <section className="flex flex-col gap-4">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat label="Tasks on the board" value={s?.total} />
        <Stat label="Completed" value={s?.done} />
        <Stat label="Yours" value={s?.mine} />
        <Stat label="Yours still open" value={s?.mine_open} />
      </div>

      <form onSubmit={add} className="flex flex-col gap-2 rounded-xl border border-line bg-surface p-3 sm:flex-row">
        <Input
          value={title}
          onChange={(e) => {
            setTitle(e.target.value);
            onTyping();
          }}
          placeholder="What needs doing?"
          maxLength={200}
          aria-label="New task"
        />
        <div className="flex gap-2">
          <Select value={priority} onChange={(e) => setPriority(e.target.value as Priority)} aria-label="Priority">
            <option value="low">Low</option>
            <option value="normal">Normal</option>
            <option value="high">High</option>
          </Select>
          <Button type="submit" variant="primary" loading={adding} disabled={!title.trim()} className="shrink-0">
            <Icon name="plus" /> Add
          </Button>
        </div>
      </form>
      <p className="-mt-2 h-4 px-1 text-xs text-muted" aria-live="polite">
        {typing.length > 0 && `${typing.join(', ')} ${typing.length === 1 ? 'is' : 'are'} typing…`}
      </p>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="inline-flex rounded-lg border border-line bg-sunken p-0.5 text-xs font-medium">
          {FILTERS.map((f) => (
            <button
              key={f.value}
              onClick={() => setFilter(f.value)}
              className={cx('rounded-md px-3 py-1.5 transition', filter === f.value ? 'bg-surface text-ink shadow-sm ring-1 ring-line' : 'text-muted hover:text-ink')}
            >
              {f.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Icon name="search" className="pointer-events-none absolute top-1/2 left-2.5 -translate-y-1/2 text-muted" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search" className="w-44 py-1.5 pl-8" aria-label="Search tasks" />
          </div>
          <span className={cx('inline-flex items-center gap-1.5 text-xs', board.live ? 'text-brand-strong' : 'text-muted')} title="Realtime subscription">
            <span className={cx('size-2 rounded-full', board.live ? 'bg-brand' : 'bg-line-strong')} />
            {board.live ? 'Live' : 'Connecting'}
          </span>
        </div>
      </div>

      {(board.error || actionError) && <Alert>{board.error ?? actionError}</Alert>}

      <ul className="flex flex-col divide-y divide-line overflow-hidden rounded-xl border border-line bg-surface">
        {board.loading && <li className="px-4 py-10 text-center text-sm text-muted">Loading tasks…</li>}
        {!board.loading && board.tasks.length === 0 && (
          <li className="px-4 py-10 text-center text-sm text-muted">{search || filter !== 'all' ? 'No tasks match.' : 'No tasks yet — add the first one above.'}</li>
        )}
        {board.tasks.map((task) => (
          <TaskRow
            key={task.id}
            task={task}
            mine={task.user_id === userId}
            onToggle={() => act(() => board.toggleTask(task))}
            onPriority={(p) => act(() => board.setPriority(task, p))}
            onRename={(t) => act(() => board.renameTask(task, t))}
            onDelete={() => act(() => board.deleteTask(task))}
          />
        ))}
      </ul>

      {myDone && (
        <div className="flex justify-end">
          <Button variant="ghost" onClick={() => act(board.clearCompleted)}>
            <Icon name="trash" /> Clear my completed tasks
          </Button>
        </div>
      )}
    </section>
  );
}

function Stat({ label, value }: { label: string; value: number | undefined }) {
  return (
    <div className="rounded-xl border border-line bg-surface px-4 py-3">
      <p className="text-2xl font-semibold tabular-nums">{value ?? '–'}</p>
      <p className="text-xs text-muted">{label}</p>
    </div>
  );
}

function TaskRow({
  task,
  mine,
  onToggle,
  onPriority,
  onRename,
  onDelete,
}: {
  task: TaskWithAuthor;
  mine: boolean;
  onToggle: () => void;
  onPriority: (p: Priority) => void;
  onRename: (title: string) => void;
  onDelete: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(task.title);
  const author = task.author?.username || 'someone';

  const commit = () => {
    setEditing(false);
    if (draft.trim() && draft.trim() !== task.title) onRename(draft.trim());
    else setDraft(task.title);
  };

  return (
    <li className="group flex items-center gap-3 px-4 py-3">
      <button
        onClick={onToggle}
        disabled={!mine}
        title={mine ? (task.done ? 'Mark as open' : 'Mark as done') : 'Only the author can change this task (row level security)'}
        className={cx(
          'inline-flex size-5 shrink-0 items-center justify-center rounded-md border transition',
          task.done ? 'border-brand bg-brand text-brand-ink' : 'border-line-strong bg-surface',
          mine ? 'hover:border-brand' : 'cursor-not-allowed opacity-60',
        )}
        aria-label={task.done ? 'Mark as open' : 'Mark as done'}
      >
        {task.done && <Icon name="check" className="size-3.5" />}
      </button>

      <div className="min-w-0 flex-1">
        {editing ? (
          <Input
            autoFocus
            value={draft}
            maxLength={200}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => {
              if (e.key === 'Enter') commit();
              if (e.key === 'Escape') {
                setDraft(task.title);
                setEditing(false);
              }
            }}
            className="py-1"
          />
        ) : (
          <p
            onDoubleClick={() => mine && setEditing(true)}
            className={cx('truncate text-sm', task.done && 'text-muted line-through')}
            title={mine ? 'Double-click to rename' : task.title}
          >
            {task.title}
          </p>
        )}
        <p className="mt-0.5 flex items-center gap-1.5 text-xs text-muted">
          <Avatar path={task.author?.avatar_path} name={author} size={16} />
          {mine ? 'You' : author} · {timeAgo(task.created_at)}
        </p>
      </div>

      {mine ? (
        <Select
          value={task.priority}
          onChange={(e) => onPriority(e.target.value as Priority)}
          className={cx('border-transparent py-1 text-xs font-medium', PRIORITY_STYLE[task.priority])}
          aria-label="Priority"
        >
          <option value="low">Low</option>
          <option value="normal">Normal</option>
          <option value="high">High</option>
        </Select>
      ) : (
        <span className={cx('rounded-lg px-2.5 py-1 text-xs font-medium capitalize', PRIORITY_STYLE[task.priority])}>{task.priority}</span>
      )}

      {mine && (
        <button onClick={onDelete} className="rounded-md p-1.5 text-muted opacity-0 transition group-hover:opacity-100 hover:bg-danger-soft hover:text-danger focus:opacity-100" aria-label="Delete task">
          <Icon name="trash" />
        </button>
      )}
    </li>
  );
}
